import { getModel } from '../../model/registry'
import { useState, useEffect } from 'react'
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useSnapshot } from 'valtio'
import styled from 'styled-components'
import portalTypes from '../../model/portalTypes'

import Loader from '../../components/generic/Loader'
import PortalPasscodeScreen from './PortalPasscodeScreen'
import PortalSetPasscode from './PortalSetPasscode'
import { portalState, usePortal } from '../../hooks/usePortal'
import { Icon_Home } from '../../styles/icons'
import Sidebar from '../../components/sidebar/Sidebar'
import { Container, Line } from '../../components/generic/Layout'
import useTranslation from '../../hooks/useTranslation'
import HeaderSidebarToggle from '../../components/header/HeaderSidebarToggle'
import MobileNavBar, { DeadSpace } from '../../components/mobileNav/MobileNavBar'
import useScreenSize from '../../hooks/useScreenSize'

const AppArea = styled(Line)`
    min-height: 100vh;
`

const PageContent = styled.div`
    flex: 1 0 0%;
    min-width: 0;
    color: ${({ theme }) => theme.font?.color};
    background: ${({ theme }) => theme.area?.background};
`

const PortalHeader = styled.header`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.border?.color || '#e5e7eb'};
`

const PortalTitle = styled.h1`
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
`

const PortalLayout = () => {
    const { token } = useParams()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [requiresPasscode, setRequiresPasscode] = useState(false)
    const [showPasscodeSetup, setShowPasscodeSetup] = useState(false)

    const { resolvePortal, authenticatePortal, restorePortalSession } = usePortal()
    const snap = useSnapshot(portalState)
    const { t } = useTranslation()
    const { isMobile } = useScreenSize()
    const location = useLocation()

    const contactDisplay = snap.contactName || snap.name
    const portalTitle = isMobile
        ? (contactDisplay || '')
        : contactDisplay
            ? `${t(`portal.type.${snap.type}`)} ${t('portal.header.for')} ${contactDisplay}`
            : t(`portal.type.${snap.type}`)


    const navigateHome = (type) => {
        const home = portalTypes[type]?.home || 'welcome'
        if (location.pathname === `/portal/${token}`) {
            navigate(`/portal/${token}/${home}`, { replace: true })
        }
    }

    useEffect(() => {
        const init = async () => {
            // If already authenticated in this session, skip resolve/auth
            if (snap.portalId || restorePortalSession()) {
                navigateHome(portalState.type)
                setLoading(false)
                return
            }

            const resolved = await resolvePortal(token)
            if (resolved.error) {
                navigate('/portal-error', { state: { error: resolved.error } })
                return
            }

            // Portal types with no navigation (invitation, joinOrganization, resetPassword)
            // never use a passcode — authenticate directly with the token.
            if (portalTypes[resolved.type]?.showNavigation === false) {
                const auth = await authenticatePortal(token)
                if (auth.error) {
                    navigate('/portal-error', { state: { error: auth.error } })
                    return
                }
                navigateHome(portalState.type)
                setLoading(false)
                return
            }

            if (resolved.passcodeSet === false) {
                setShowPasscodeSetup(true)
                setLoading(false)
                return
            }

            if (resolved.passcodeSet === true) {
                setRequiresPasscode(true)
                setLoading(false)
                return
            }

            const auth = await authenticatePortal(token)
            if (auth.error) {
                navigate('/portal-error', { state: { error: auth.error } })
                return
            }

            navigateHome(portalState.type)
            setLoading(false)
        }

        init()
    }, [token])

    const handlePasscodeSet = async (passcode) => {
        const result = await authenticatePortal(token, passcode)
        if (result.error) return
        setShowPasscodeSetup(false)
        navigateHome(portalState.type)
    }

    const handlePasscodeExists = () => {
        setShowPasscodeSetup(false)
        setRequiresPasscode(true)
    }

    const handlePasscode = async (passcode) => {
        const result = await authenticatePortal(token, passcode)
        if (result.error) return result.error
        setRequiresPasscode(false)
        navigateHome(portalState.type)
        return null
    }

    if (loading) return <Loader />
    if (showPasscodeSetup) return <PortalSetPasscode onComplete={handlePasscodeSet} onPasscodeExists={handlePasscodeExists} portalToken={token} />
    if (requiresPasscode) return <PortalPasscodeScreen onSubmit={handlePasscode} portalToken={token} />

    const showNavigation = portalTypes[snap.type]?.showNavigation

    if (showNavigation === false) {
        return (
                <PageContent>
                    <main>
                        <Outlet />
                    </main>
                </PageContent>
        )
    }

    // Populate sidebarData.portal and mobileNavBarData.portal based on portal type,
    // prepending a Home item that links back to the portal welcome page.
    const homeItem = { title: 'all.menu.home', icon: Icon_Home, path: `/portal/${token}/welcome` }
    const emptyItem = { title: '', path: '', icon: null }

    getModel('sidebarData').portal = [homeItem, ...(getModel('sidebarData')[snap.type] || [])]

    const typeNavItems = (getModel('mobileNavBarData')[snap.type] || []).filter(item => item.title !== '')
    const combined = [homeItem, ...typeNavItems]
    getModel('mobileNavBarData').portal = combined.length < 4
        ? [...combined, ...Array(4 - combined.length).fill(emptyItem)]
        : combined

    return (
        <Container>
            <AppArea>
                <Sidebar />
                <PageContent>
                    <PortalHeader>
                        <HeaderSidebarToggle />
                        <PortalTitle>{portalTitle}</PortalTitle>
                    </PortalHeader>
                    <main>
                        <Outlet />
                    </main>
                    <MobileNavBar />
                    <DeadSpace />
                </PageContent>
            </AppArea>
        </Container>
    )
}

export default PortalLayout
